import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  addWhatsAppAIExcludedNumber,
  createWhatsAppConnection,
  disconnectWhatsAppConnection,
  getCurrentWhatsAppConnection,
  getWhatsAppAISettings,
  getWhatsAppConnectionQR,
  getWhatsAppConnectionStatus,
  reconnectWhatsAppConnection,
  removeWhatsAppAIExcludedNumber,
  updateWhatsAppAISettings,
} from "../api/whatsapp";
import { getApiError } from "../api/errors";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

const POLL_INTERVAL_MS = 3000;
const ACTIVE_STATUSES = new Set([
  "starting",
  "connecting",
  "qr_ready",
  "reconnecting",
]);

function statusColor(status) {
  if (status === "connected") return "success";
  if (status === "failed" || status === "logged_out") return "error";
  if (ACTIVE_STATUSES.has(status)) return "warning";
  return "default";
}

export default function WhatsAppIntegration() {
  const { selectedWorkspace, selectedStore, canManage } = useAuth();
  const [connection, setConnection] = useState(null);
  const [aiSettings, setAISettings] = useState({
    enabled: false,
    business_only: true,
    groups_ignored: true,
    excluded_numbers: [],
  });
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [aiWorking, setAIWorking] = useState(false);
  const [error, setError] = useState("");
  const [disconnectDialog, setDisconnectDialog] = useState(false);
  const [excludedPhone, setExcludedPhone] = useState("");
  const [excludedLabel, setExcludedLabel] = useState("");

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [connectionResponse, settingsResponse] = await Promise.all([
        getCurrentWhatsAppConnection(),
        getWhatsAppAISettings(),
      ]);
      setConnection(connectionResponse.data || null);
      setAISettings(settingsResponse.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load WhatsApp settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent, selectedWorkspace?.public_id, selectedStore?.public_id]);

  const refresh = useCallback(async () => {
    if (!connection?.public_id) return;
    try {
      const response = connection.status === "qr_ready"
        ? await getWhatsAppConnectionQR(connection.public_id)
        : await getWhatsAppConnectionStatus(connection.public_id);
      setConnection(response.data);
      setError("");
    } catch (requestError) {
      setError(getApiError(requestError, "Could not refresh WhatsApp status."));
    }
  }, [connection?.public_id, connection?.status]);

  useEffect(() => {
    if (!connection?.public_id || !ACTIVE_STATUSES.has(connection.status)) return undefined;
    const timer = window.setInterval(refresh, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [connection?.public_id, connection?.status, refresh]);

  const startConnection = async () => {
    setWorking(true);
    setError("");
    try {
      const response = await createWhatsAppConnection();
      setConnection(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not start WhatsApp connection."));
    } finally {
      setWorking(false);
    }
  };

  const reconnect = async () => {
    if (!connection?.public_id) return;
    setWorking(true);
    setError("");
    try {
      const response = await reconnectWhatsAppConnection(connection.public_id);
      setConnection(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not reconnect WhatsApp."));
    } finally {
      setWorking(false);
    }
  };

  const disconnect = async (logout) => {
    if (!connection?.public_id) return;
    setWorking(true);
    setError("");
    try {
      const response = await disconnectWhatsAppConnection(connection.public_id, logout);
      setConnection(logout ? null : response.data);
      setDisconnectDialog(false);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not disconnect WhatsApp."));
    } finally {
      setWorking(false);
    }
  };

  const toggleAI = async (enabled) => {
    setAIWorking(true);
    setError("");
    try {
      const response = await updateWhatsAppAISettings(enabled);
      setAISettings(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update AI auto reply."));
    } finally {
      setAIWorking(false);
    }
  };

  const addExcludedNumber = async () => {
    if (!excludedPhone.trim()) return;
    setAIWorking(true);
    setError("");
    try {
      await addWhatsAppAIExcludedNumber(
        excludedPhone.trim(),
        excludedLabel.trim() || null,
      );
      const response = await getWhatsAppAISettings();
      setAISettings(response.data);
      setExcludedPhone("");
      setExcludedLabel("");
    } catch (requestError) {
      setError(getApiError(requestError, "Could not exclude this number."));
    } finally {
      setAIWorking(false);
    }
  };

  const removeExcludedNumber = async (publicId) => {
    setAIWorking(true);
    setError("");
    try {
      await removeWhatsAppAIExcludedNumber(publicId);
      setAISettings((current) => ({
        ...current,
        excluded_numbers: current.excluded_numbers.filter(
          (item) => item.public_id !== publicId,
        ),
      }));
    } catch (requestError) {
      setError(getApiError(requestError, "Could not remove this number."));
    } finally {
      setAIWorking(false);
    }
  };

  const qrExpired = useMemo(() => {
    const expiresAt = connection?.qr?.expires_at;
    return expiresAt ? new Date(expiresAt).getTime() <= Date.now() : false;
  }, [connection?.qr?.expires_at]);

  return (
    <MainLayout>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={900} letterSpacing={-0.7}>
            WhatsApp Connection
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Connect this store and let the AI answer customer chats automatically.
          </Typography>
        </Box>
        {connection && (
          <Chip
            label={titleCase(connection.status)}
            color={statusColor(connection.status)}
            sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", md: "center" } }}
          />
        )}
      </Stack>

      <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
        This QR connection is for the controlled pilot. Keep the linked phone online and do not expose the gateway port publicly.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ minHeight: 320, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </CardContent>
        </Card>
      ) : !connection ? (
        <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: { xs: 3, md: 5 }, textAlign: "center" }}>
            <Typography fontSize={54}>📱</Typography>
            <Typography variant="h5" fontWeight={900} sx={{ mt: 1 }}>
              Connect your store WhatsApp
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              Create a secure session, then scan the QR code from WhatsApp Linked Devices on the store phone.
            </Typography>
            <Button variant="contained" size="large" onClick={startConnection} disabled={working}>
              {working ? "Starting…" : "Connect WhatsApp"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 4, border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={4} alignItems="stretch">
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" gap={2} alignItems="center">
                  <Box>
                    <Typography variant="h5" fontWeight={900}>
                      {connection.display_name || "Store WhatsApp"}
                    </Typography>
                    <Typography color="text.secondary">
                      {connection.phone_number ? `+${connection.phone_number}` : "Waiting for QR scan"}
                    </Typography>
                  </Box>
                  <Chip label={titleCase(connection.status)} color={statusColor(connection.status)} />
                </Stack>

                <Divider sx={{ my: 3 }} />
                <Stack spacing={1.4}>
                  <Row label="Store" value={selectedStore?.name || "Current store"} />
                  <Row label="Connected at" value={connection.connected_at ? formatDate(connection.connected_at) : "Not connected"} />
                  <Row label="Last status" value={connection.last_status_at ? formatDate(connection.last_status_at) : "Not available"} />
                </Stack>

                {connection.last_error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 3 }}>
                    {connection.last_error}
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3 }}>
                  <Button variant="outlined" onClick={refresh} disabled={working}>
                    Refresh status
                  </Button>
                  {connection.status !== "connected" && (
                    <Button variant="contained" onClick={reconnect} disabled={working}>
                      Reconnect
                    </Button>
                  )}
                  <Button color="error" variant="outlined" onClick={() => setDisconnectDialog(true)} disabled={working}>
                    Disconnect
                  </Button>
                </Stack>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 360,
                  borderRadius: 4,
                  bgcolor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  display: "grid",
                  placeItems: "center",
                  p: 3,
                  textAlign: "center",
                }}
              >
                {connection.status === "connected" ? (
                  <Box>
                    <Typography fontSize={66}>✅</Typography>
                    <Typography variant="h5" fontWeight={900}>WhatsApp connected</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      Customer messages can now flow into Conversations and use your AI reply settings.
                    </Typography>
                  </Box>
                ) : connection.qr?.data_url && !qrExpired ? (
                  <Box>
                    <Box
                      component="img"
                      src={connection.qr.data_url}
                      alt="WhatsApp QR code"
                      sx={{ width: { xs: 250, sm: 300 }, maxWidth: "100%", borderRadius: 2 }}
                    />
                    <Typography fontWeight={900} sx={{ mt: 2 }}>Scan with WhatsApp</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Settings → Linked Devices → Link a Device
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CircularProgress size={34} />
                    <Typography fontWeight={900} sx={{ mt: 2 }}>
                      {qrExpired ? "QR expired—generating a new one" : "Waiting for QR code"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Status refreshes automatically every few seconds.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!loading && (
        <Card sx={{ mt: 3, borderRadius: 4, border: "1px solid #e2e8f0" }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              gap={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Box>
                <Typography variant="h5" fontWeight={900}>
                  AI automatic replies
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                  Choose whether the AI should answer eligible one-to-one customer chats. Groups and excluded contacts remain untouched.
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography fontWeight={900} color={aiSettings.enabled ? "success.main" : "text.secondary"}>
                  {aiSettings.enabled ? "ON" : "OFF"}
                </Typography>
                <Switch
                  checked={Boolean(aiSettings.enabled)}
                  onChange={(event) => toggleAI(event.target.checked)}
                  disabled={!canManage || aiWorking}
                  inputProps={{ "aria-label": "AI automatic replies" }}
                />
              </Stack>
            </Stack>

            <Alert severity="info" sx={{ mt: 2.5, borderRadius: 3 }}>
              For a personal number, add family or friends below. You can also disable AI for one chat from Conversations.
            </Alert>

            <Divider sx={{ my: 3 }} />
            <Typography fontWeight={900} sx={{ mb: 1.5 }}>
              Numbers the AI must never answer
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
              <TextField
                label="Phone number"
                placeholder="03 123 456 or +961..."
                value={excludedPhone}
                onChange={(event) => setExcludedPhone(event.target.value)}
                disabled={!canManage || aiWorking}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Name (optional)"
                placeholder="Mom, family, friend"
                value={excludedLabel}
                onChange={(event) => setExcludedLabel(event.target.value)}
                disabled={!canManage || aiWorking}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                onClick={addExcludedNumber}
                disabled={!canManage || aiWorking || !excludedPhone.trim()}
                sx={{ minWidth: 110 }}
              >
                Add
              </Button>
            </Stack>

            {aiSettings.excluded_numbers.length === 0 ? (
              <Typography color="text.secondary" sx={{ mt: 2 }}>
                No excluded numbers yet.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 2 }}>
                {aiSettings.excluded_numbers.map((item) => (
                  <Stack
                    key={item.public_id}
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    gap={1}
                    sx={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      px: 2,
                      py: 1.4,
                    }}
                  >
                    <Box>
                      <Typography fontWeight={900}>{item.label || "Excluded contact"}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.phone}
                      </Typography>
                    </Box>
                    {canManage && (
                      <Button
                        color="error"
                        size="small"
                        onClick={() => removeExcludedNumber(item.public_id)}
                        disabled={aiWorking}
                      >
                        Remove
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={disconnectDialog} onClose={() => !working && setDisconnectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disconnect WhatsApp</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            “Pause” keeps encrypted session credentials for reconnection. “Log out” removes the linked-device session and requires a new QR scan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialog(false)} disabled={working}>Cancel</Button>
          <Button onClick={() => disconnect(false)} disabled={working}>Pause session</Button>
          <Button color="error" variant="contained" onClick={() => disconnect(true)} disabled={working}>
            Log out WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}

function Row({ label, value, mono = false }) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={0.5}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography fontWeight={800} sx={mono ? { fontFamily: "monospace", overflowWrap: "anywhere" } : undefined}>
        {value}
      </Typography>
    </Stack>
  );
}
